import { Auth0Provider } from '@auth0/auth0-react';
import { ReactNode } from 'react';
import { AUTH0_CLIENT_DOMAIN, AUTH0_CLIENT_ID, AUTH0_REDIRECT_URL, SKIP_QA_SIGN_ON } from '../config';

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Auth0 Provider wrapper for prosci_portal
 * Handles the Auth0 SDK configuration and redirect callbacks
 *
 * In QA mode (SKIP_QA_SIGN_ON=true), this component just renders children
 * without initializing Auth0.
 */
function Auth0ProviderWithNavigate({ children }: AuthProviderProps) {
  // In QA mode, skip Auth0 initialization entirely
  if (SKIP_QA_SIGN_ON) {
    return <>{children}</>;
  }

  // Validate Auth0 configuration
  if (!AUTH0_CLIENT_DOMAIN || !AUTH0_CLIENT_ID) {
    console.error('Missing Auth0 configuration. Please set VITE_AUTH0_CLIENT_DOMAIN and VITE_AUTH0_CLIENT_ID');
    return <>{children}</>;
  }

  const redirectUri = AUTH0_REDIRECT_URL || `${window.location.origin}/auth/callback`;

  return (
    <Auth0Provider
      domain={AUTH0_CLIENT_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: redirectUri,
        scope: 'openid profile email offline_access',
        'ext-source': 'portal'
      }}
      // NOTE: IdP-initiated SSO is handled in AuthCallback.tsx by detecting flows
      // without a 'state' parameter and triggering an SP-initiated redirect.
      // The __enableIdPInitiatedLogin flag does NOT work with auth0-spa-js.
      // See: https://github.com/auth0/auth0-spa-js/issues/213
      onRedirectCallback={(appState) => {
        // Store the return URL for AuthContext to use after backend session is created
        // We don't navigate here because AuthContext needs to create the backend session first
        // If we navigate immediately, the fetch request gets aborted with "Load failed"
        if (appState?.returnTo) {
          sessionStorage.setItem('auth0_return_to', appState.returnTo);
        } else {
          sessionStorage.setItem('auth0_return_to', '/');
        }
        // Clean up URL parameters without navigating away
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete('code');
        searchParams.delete('state');
        searchParams.delete('error');
        searchParams.delete('error_description');

        const newUrl = searchParams.toString()
          ? `${window.location.pathname}?${searchParams.toString()}`
          : window.location.pathname;

        // Replace current URL to clean up Auth0 parameters, but stay on the callback page
        window.history.replaceState({}, '', newUrl);
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
    >
      {children}
    </Auth0Provider>
  );
}

export function AuthProviderWrapper({ children }: AuthProviderProps) {
  return <Auth0ProviderWithNavigate>{children}</Auth0ProviderWithNavigate>;
}

export default AuthProviderWrapper;
